import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState, mapActions } from "pinia";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";

import {
    AgencyRoles,
    ApplicationEnum,
    InternalReferralSource,
    PalliativeSourceOfReferral,
    Relationships,
    SourceOfReferral
} from "../../../../../../enums/enums";
import { InternalReferralSourcePal } from "../../../../../../enums/InternalReferralSourcePalEnum";
import { EnumsService } from "../../../../../../services/enums.service";
import { UserService } from "../../../../../../services/user-service";
import { VendorsService } from "../../../../../../services/vendor-service";
import { PhysiciansService } from "../../../../../../services/physician-service";
import { IGetUsersByRoleQuery, IUserResult } from "../../../../../../resources-vue/vue-interfaces/i-user";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { formatName } from "../../../../../../common/format-name";
import { IGetVendorSlim } from "../../../../../../resources-vue/vue-interfaces/i-vendor";
import { useConvertToPendingPal } from "../../../store/useConvertToPendingPal";
import { formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";

import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import CommentInput from "../../../../../../resources-vue/vue-custom-elements/CommentInput/CommentInput.vue";
import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import PendingReason from "../../../../../../resources-vue/vue-custom-elements/PendingReason/PendingReason.vue";

export default defineComponent({
    components: {
        TypeaheadInput,
        CommentInput,
        DatePicker,
        FormSection,
        PendingReason
    },

    data() {
        return {
            _enumService: null,
            _usersService: null,
            _vendorsService: null,
            _physicianService: null,
            _subscription: null,
            _hospiceReferralSourceOrder: ["Physician", "Facility", "Internal", "Other"],
            _palliativeReferralSourceOrder: ["Provider", "Facility", "Internal", "Other"],
            communityLiaison: null,
            internalReferralSourceName: null,
            physician: null,
            facility: null,
            internalReferralSourceModel: null,
            referringFacility: null,
            referringProvider: null,
            ignoreFacilityChange: false,
            ignorePhysicianChange: false,
            referralSources: [],
            relationships: Array.from(Relationships.enumWithDescriptions.values()),
            showRelationshipDescription: false,
            InternalReferralSourceEnum: Array.from(InternalReferralSourcePal.enumWithDescriptions.values()),
            SourceOfReferrals: Array.from(SourceOfReferral.enumWithDescriptions.values()),
            AgencyRoles: Array.from(AgencyRoles.enumWithDescriptions.values()),
            PalliativeSourceOfReferrals: Array.from(PalliativeSourceOfReferral.enumWithDescriptions.values()),
            commentInputClass: { input: "w-80" },
            datePickerOptions: {} as DatepickerOptions,
            roles: null,
            formatDateInput,
            v$: useVuelidate()
        };
    },

    validations() {
        const referralDateNotBeforeInquiryDate = helpers.withMessage(
            "The referral date must be on or after the inquiry date.",
            (value: string | null) => {
                // if referral date is empty, it's invalid here
                if (!value) return false;

                // if inquiry date is not provided, allow ANY referral date
                if (!this.patientDetails.referralInformation.inquiryDate) return true;

                // compare dates
                return new Date(value) >= new Date(this.patientDetails.referralInformation.inquiryDate);
            }
        );
        return {
            patientDetails: {
                referralInformation: {
                    referralDate: {
                        required: helpers.withMessage("Referral Date is required.", required),
                        referralDateNotBeforeInquiryDate
                    },
                    referralReason: { required }
                }
            }
        };
    },

    async created() {
        this.roles = this.AgencyRoles;
        this._enumService = Container.instance.get(EnumsService);
        this._usersService = Container.instance.get(UserService);
        this._vendorsService = Container.instance.get(VendorsService);
        this._physicianService = Container.instance.get(PhysiciansService);
        await this.initOptions();
        if (!!this.patientDetails) {
            this.initValuesFromModel();
            this.v$.$touch();
        }
    },

    computed: {
        ...mapState(useConvertToPendingPal, [
            "patientDetails",
            "accountId",
            "application",
            "patientId",
            "patientOptions",
            "errors"
        ])
    },

    watch: {
        async facility() {
            if (!this.facility || this.ignoreFacilityChange) {
                this.ignoreFacilityChange = false;
                return;
            }
            this.patientDetails.referralInformation.facilityReferralSourceId = this.facility.value;
            this.patientDetails.referralInformation.facilityReferralSourceName = this.facility.name;
            if (!this.facility.value) {
                return;
            }
            const facilityPayload = {
                id: this.facility.value,
                accountId: this.accountId,
                application: this.application
            };
            let selectedFacility = await this._vendorsService.getVendorById(facilityPayload);
            this.communityLiaison = {
                name: selectedFacility.communityLiaisonName,
                value: selectedFacility.communityLiaisonId
            };
        },
        physician() {
            if (!this.physician || this.ignorePhysicianChange) {
                this.ignorePhysicianChange = false;
                return;
            }
            let npiIndex = this.physician?.name?.indexOf(" - NPI");
            if (npiIndex > 0 && this.physician?.name) {
                let name = this.physician.name?.slice(0, npiIndex + 1);
                this.patientDetails.referralInformation.physicianReferralSourceName = name;
            } else {
                this.patientDetails.referralInformation.physicianReferralSourceName = this.physician.name;
            }
            this.autofillCommunityLiaison(this.physician.value);
        },
        communityLiaison() {
            if (this.communityLiaison) {
                this.patientDetails.referralInformation.communityLiaisonId = this.communityLiaison.value as string;
                this.patientDetails.referralInformation.communityLiaison = this.communityLiaison.name as string;
            } else {
                this.patientDetails.referralInformation.communityLiaisonId = null;
                this.patientDetails.referralInformation.communityLiaison = null;
            }
        },
        internalReferralSourceName() {
            if (this.internalReferralSourceName) {
                this.patientDetails.referralInformation.internalReferralSourceId =
                    this.internalReferralSourceName.value;
                this.patientDetails.referralInformation.internalReferralSourceName =
                    this.internalReferralSourceName.name;
            }
        },

        referringProvider() {
            if (this.referringProvider) {
                this.patientDetails.referralInformation.referringProviderId = this.referringProvider.value;
                this.patientDetails.referralInformation.referringProviderName = this.referringProvider.name;
            }
        },

        referringFacility() {
            if (this.referringFacility) {
                this.patientDetails.referralInformation.referringFacilityId = this.referringFacility.value;
                this.patientDetails.referralInformation.referringFacilityName = this.referringFacility.name;
            }
        }
    },

    methods: {
        ...mapActions(useConvertToPendingPal, {
            setErrors: "SET_NEW_ERRORS"
        }),
        async initOptions() {
            this.getSourceOfReferral();
        },
        async getSourceOfReferral() {
            this.referralSources = this._palliativeReferralSourceOrder.map((referralSource) => {
                return PalliativeSourceOfReferral.enumWithDescriptions.get(referralSource);
            });
        },
        initValuesFromModel() {
            if (!!this.patientDetails) {
                if (!this.patientDetails.referralInformation.sourceOfReferral && this.referralSources.length > 0) {
                    this.patientDetails.referralInformation.sourceOfReferral = this.referralSources[0].value as number;
                }
                if (
                    this.patientDetails.referralInformation.physicianReferralSourceId &&
                    this.patientDetails.referralInformation.physicianReferralSourceName
                ) {
                    this.ignorePhysicianChange = true;
                    this.physician = {
                        name: this.patientDetails.referralInformation.physicianReferralSourceName,
                        value: this.patientDetails.referralInformation.physicianReferralSourceId
                    };
                }
                if (
                    this.patientDetails.referralInformation.facilityReferralSourceId &&
                    this.patientDetails.referralInformation.facilityReferralSourceName
                ) {
                    this.ignoreFacilityChange = true;
                    this.facility = {
                        name: this.patientDetails.referralInformation.facilityReferralSourceName,
                        value: this.patientDetails.referralInformation.facilityReferralSourceId
                    };
                }
                if (
                    this.patientDetails.referralInformation.internalReferralSourceId &&
                    this.patientDetails.referralInformation.internalReferralSourceName
                ) {
                    this.internalReferralSourceName = {
                        name: this.patientDetails.referralInformation.internalReferralSourceName,
                        value: this.patientDetails.referralInformation.internalReferralSourceId
                    };
                }
                if (
                    this.patientDetails.referralInformation.communityLiaisonId &&
                    this.patientDetails.referralInformation.communityLiaison
                ) {
                    this.communityLiaison = {
                        name: this.patientDetails.referralInformation.communityLiaison,
                        value: this.patientDetails.referralInformation.communityLiaisonId
                    };
                }
                if (
                    this.patientDetails.referralInformation.referringFacilityId &&
                    this.patientDetails.referralInformation.referringFacilityName
                ) {
                    this.referringFacility = {
                        name: this.patientDetails.referralInformation.referringFacilityName,
                        value: this.patientDetails.referralInformation.referringFacilityId
                    };
                }
                if (
                    this.patientDetails.referralInformation.referringProviderId &&
                    this.patientDetails.referralInformation.referringProviderName
                ) {
                    this.referringProvider = {
                        name: this.patientDetails.referralInformation.referringProviderName,
                        value: this.patientDetails.referralInformation.referringProviderId
                    };
                }
            }
        },

        async getUsersByRole(filter: string, limit: number) {
            return this.userToTypeahead(
                await this._usersService.getUsersByRole(this.getUserQuery("Community Liaison Officer", true, filter))
            );
        },
        userToTypeahead(users: IUserResult[]): ITypeaheadOptions[] {
            return users.map((user) => ({
                name: formatName(user.firstName, user.lastName),
                value: user.id
            }));
        },
        getUserQuery(userRole: string, include: boolean, filter: string): IGetUsersByRoleQuery {
            let roleCode = this.roles.filter((role: any) => role.name === userRole)[0].value;
            return {
                role: [roleCode] as number[],
                include: include,
                term: filter,
                providerIds: [this.patientOptions.data.providerId],
                application: this.application
            };
        },
        async getPhysicianList(filter: string, limit: number) {
            let data = await this._physicianService.getPhysiciansByName({
                name: filter,
                page: 1,
                pageLength: limit,
                providerIds: [this.patientOptions.data.providerId],
                application: this.application,
                accountId: this.accountId
            });
            return data.items?.map((item: any) => {
                let splitName = item.name.split(" ");
                return {
                    name: formatName(splitName[0], splitName[1]) + (item.npi ? ` - NPI: ${item.npi}` : ""),
                    value: item.id
                };
            });
        },
        async getVendorList(filter: string, limit: number) {
            const payload = {
                accountId: this.accountId,
                name: filter,
                providerIds: [this.patientOptions.data.providerId],
                application: this.application
            };
            let facilityList = await this._vendorsService.getVendorByTerm(payload);
            return this.facilityToTypeahead(facilityList);
        },
        sourceOfReferralUpdated() {
            // Facility Referral Source
            this.facility = {
                name: null,
                value: null
            };
            this.patientDetails.referralInformation.facilityReferralSourceName = null;
            this.communityLiaison = {
                name: null,
                value: null
            };
            // Physician Referral Source
            this.physician = {
                name: null,
                value: null
            };
            this.patientDetails.referralInformation.physicianReferralSourceName = null;
            // Other Referral Source
            this.patientDetails.referralInformation.otherReferralSourceName = null;
            this.patientDetails.referralInformation.otherReferralSourceRelationship = null;
            this.patientDetails.referralInformation.otherReferralSourceRelationship = null;
            // Internal Referral Source
            this.internalReferralSourceName = {
                name: null,
                value: null
            };
        },
        async autofillCommunityLiaison(physicianId: string) {
            if (!physicianId) {
                return;
            }
            const payload = {
                physicianId,
                application: this.application,
                accountId: this.accountId
            };
            let physician = await this._physicianService.getPhysicianById(payload);
            this.communityLiaison = {
                name: physician?.communityLiaisonName,
                value: physician?.communityLiaisonId
            };
        },
        facilityToTypeahead(facilities: IGetVendorSlim[]): ITypeaheadOptions[] {
            return facilities.map((facility) => ({
                name: facility.name,
                value: facility.id
            }));
        },
        resultUpdatedAppended(value: string) {
            this.patientDetails.referralInformation.comments.concat(value);
        },
        resultUpdated(value: string) {
            this.patientDetails.referralInformation.comments = value;
        },
        updateFacility(newValue: any) {
            this.facility = newValue;
        },
        updateReferringFacility(newValue: any) {
            this.referringFacility = newValue;
        },
        updateReferringProvider(newValue: any) {
            this.referringProvider = newValue;
        },
        updateCommunityLiaison(newValue: any) {
            this.communityLiaison = newValue;
        },
        updatePhysician(newValue: any) {
            this.physician = newValue;
        },
        updateInternalReferralSourceName(newValue: any) {
            this.internalReferralSourceName = newValue;
        },

        onReferralSourceChange(newValue: any) {
            console.log(newValue);
            this.patientDetails.referralInformation.internalReferralSource = newValue;
        },
        handleDateChange(date: any, name: string) {
            if (name === "referralDate") {
                this.patientDetails.referralInformation.referralDate = date;
            }
            this.handleAllInput(date, "referralDate");
        },
        handleAllInput(value: any, field: string) {
            if (value.length) {
                if ([...new Set(this.errors.referralInformation.errors)].includes(field)) {
                    const newErrors = [...new Set(this.errors.referralInformation.errors)]?.filter(
                        (val: any) => val !== field
                    );
                    const payload = {
                        property: "referralInformation",
                        value: newErrors
                    };
                    this.setErrors(payload, true, true);
                }
            }
        }
    }
});
